import { Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { VehiclesService } from '../../vehicles/_services/vehicles.service';
import { WorkerVehiclesService } from '../_services/workers-vehicles.service';
import { WorkerVehicle } from '../_types/worker-vehicle';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { Tax } from '../../taxes/_types/tax';
import { TaxesService } from '../../taxes/_services/taxes.service';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { Validators } from '@angular/forms';
import { LoginService } from '../../login/login.service';

@Component({
    selector: 'app-workers-vehicle',
    templateUrl: './workers-vehicle.component.html',
    styleUrls: ['./workers-vehicle.component.scss'],
    standalone: false
})
export class WorkersVehicleComponent {

  public workerVehicleFormGroup!: RxFormGroup;

  public workerVehicle: WorkerVehicle = new WorkerVehicle();
  public vehicles: any[] = [];

  public allowWrite: boolean = false;
  public allowDelete: boolean = false;

  public total: number = 0;
  public taxAmount: number = 0;
  public managementFee: number = 0;
  public taxes: Tax[] = [];
  public loadingTaxes: boolean = false;


  constructor(
    private workerVehiclesService: WorkerVehiclesService,
    private config: DynamicDialogConfig,
    private messageService: MessageService,
    private dynamicDialogRef: DynamicDialogRef,
    private confirmationService: ConfirmationService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private vehiclesService: VehiclesService,
    private taxesService: TaxesService,
    private loginService: LoginService
  ) {
    this.workerVehicleFormGroup = this.formBuilder.formGroup<WorkerVehicle>(this.workerVehicle) as RxFormGroup;
  }

  async ngOnInit(){
    await this.loadTaxes();
    if (this.config.data.workerVehicleId) {
      this.loadWorkerVehicle(this.config.data.workerVehicleId);
    } else {
      this.workerVehicle.workerId = this.config.data.workerId;
    }
    this.loadVehicles();

    this.allowWrite = this.loginService.hasPermission("WORKERS.WRITE");
    this.allowDelete = this.loginService.hasPermission("WORKERS.DELETE");
  }

  async loadWorkerVehicle(id: string) {
    this.workerVehicle = await firstValueFrom(this.workerVehiclesService.get(id));
    this.workerVehicleFormGroup.patchValue(this.workerVehicle);

    this.calculatePrices();
  }

  async loadVehicles() {

    let params: IParamsCollection = {
      include: ['business', 'worker'],      
    }

    let filters = {
      active: new Filter(FilterOperation.equals, 'active', "", true)
    }; 

    //creamos filtros
    params.filter = FilterBuilder.fromObject(filters).build();

    let response = await firstValueFrom(this.vehiclesService.all(params));

    this.vehicles = response.data.map((vehicle: any) => {
      let ownerStr = "";
      if(vehicle.ownerType == "BUSINESS" && typeof vehicle.business != "undefined"){
        ownerStr = " (Compañía: " + vehicle.business.name + ")";
      }      
      if(vehicle.ownerType == "WORKER" && typeof vehicle.worker != "undefined"){
        ownerStr = " (Operario: " + vehicle.worker?.name + ")";
      }      

      return {
        id: vehicle.id,
        customName: vehicle.name + " - " + vehicle.plate + ownerStr,
        ownerType: vehicle.ownerType,
        notes: vehicle.notes
      };
    }
    );
  }

  async loadTaxes() {
    this.loadingTaxes = true;
    let params: IParamsCollection = {
    }

    let response = await firstValueFrom(this.taxesService.all(params));
    this.taxes = response.data;

    this.loadingTaxes = false;
  }

  async save() {
    let valid = this.formValidationService.validateForm(this.workerVehicleFormGroup);
    if (valid) {
      this.workerVehicle = Object.assign(this.workerVehicle, this.workerVehicleFormGroup.value);


      if(this.workerVehicle.deductFromWorker && (this.workerVehicle.price == null || this.workerVehicle.taxId == null)) {
        
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Debe indicar el precio y el iva a descontar al operario.' });
        return
      }

      await this.workerVehiclesService.save(this.workerVehicle).subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Vehículo asignado correctamente' });
          this.dynamicDialogRef.close();
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error al asignar el vehículo' });
        }
      });
    } else {
      document.getElementById('form')?.classList.add('formInvalid');
    }
  }
  cancel() {
    this.dynamicDialogRef.close();
  }

  delete() {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea desasignar el vehículo?",
      header: "Desasignar vehículo",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.workerVehiclesService.delete(this.workerVehicle.id).subscribe({
          next: () => {
            this.cancel();
            this.messageService.add({ closable: true, severity: 'success', detail: "Vehículo desasignado correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      }
    });
  }

  get selectedVehicle() {
    return this.vehicles.find(vehicle => vehicle.id === this.workerVehicleFormGroup.get('vehicleId')?.value);
  }

  calculatePrices() {
    if(this.workerVehicleFormGroup.controls["taxId"].value != null && this.workerVehicleFormGroup.controls["price"].value != null){
      
      let tax:any = this.taxes.find(tax => tax.id == this.workerVehicleFormGroup.controls["taxId"].value); 
      var priceWithManagentFee = this.workerVehicleFormGroup.controls["price"].value;
      if(this.workerVehicleFormGroup.controls["managementFee"].value !=null && this.workerVehicleFormGroup.controls["managementFee"].value > 0) {
        this.managementFee = (this.workerVehicleFormGroup.controls["price"].value * (this.workerVehicleFormGroup.controls["managementFee"].value / 100));
        priceWithManagentFee = priceWithManagentFee + this.managementFee;
      }
      this.taxAmount = priceWithManagentFee * (tax.percent / 100);          
      this.total = priceWithManagentFee + this.taxAmount;   
    }    

  }

  toggleFormValidations(){
    if(this.workerVehicleFormGroup.controls["deductFromWorker"].value){
      this.workerVehicleFormGroup.controls['price'].setValidators([Validators.required]);
      this.workerVehicleFormGroup.controls['taxId'].setValidators([Validators.required]);
    }else{
      this.workerVehicleFormGroup.controls['price'].setValidators(null);
      this.workerVehicleFormGroup.controls['taxId'].setValidators(null);
    }
  }
}
